import moment from 'moment';
import dayjs from 'dayjs';
import {
  PAYMENT_ORCHESTRATION_RULE_DATE_FORMAT,
  PAYMENT_ORCHESTRATION_RULE_DATE_TIME_FORMAT,
  DYNAMIC_RULES_EFFECTIVE_DATE_FORMAT,
} from './payment-orchestration.constants';

export const JAPANESE_YEN = 'JPY';
export const KOREAN_WON = 'KRW';
export const INDIAN_RUPEE = 'INR';
export const INDONESIAN_RUPIAH = 'IDR';
export const MALAYSIAN_RINGGIT = 'MYR';
export const PHILIPPINE_PESO = 'PHP';
export const VIETNAMESE_DONG = 'VND';

export const DEFAULT_CULTURE = 'en-gb';
export const US_CULTURE = 'en-us';
export const JAPAN_CULTURE = 'ja-jp';
export const KOREA_CULTURE = 'ko-kr';
export const CHINA_CULTURE = 'zh-cn';
export const TAIWAN_CULTURE = 'zh-tw';
export const HONGKONG_CULTURE = 'zh-hk';
export const THAILAND_CULTURE = 'th-th';
export const VIETNAME_CULTURE = 'vi-vn';
export const INDONESIAN_CULTURE = 'id-id';
export const MALAYSIA_CULTURE = 'ms-my';

export function dateFormatter(value) {
  return moment(value).format(PAYMENT_ORCHESTRATION_RULE_DATE_FORMAT);
}

export function dateTimeFormatter(value) {
  return moment(value).format(PAYMENT_ORCHESTRATION_RULE_DATE_TIME_FORMAT);
}

/**
 * Note: copied from cashier react
 */
export const toFixed = (value, decimal = 2) => {
  const re = new RegExp(`(\\d+\\.\\d{${decimal}})(\\d)`);
  const m = value.toString().match(re);
  return m ? parseFloat(m[1]) : value.valueOf();
};

/**
 * Note: copied from cashier react
 * @param value - Value that will be formatted
 * @param currencyCode - selected currency code
 * @param languageId - selected language, default to en-gb
 * @param decimal - number of decimal, default to 2
 * @returns - formatted string
 */
export function currencyFormatter({
  value,
  currencyCode,
  languageId = 'en-gb',
  decimal = 2,
}) {
  if (value === undefined || value === null) return value;
  if (Number.isNaN(value)) return value;

  let val = toFixed(value, decimal);

  // For IDR we don't have decimal values. So we round it off for displaying.
  // For IDR, show formatted amount for all the languages
  // For PAYMNT-3160, JPY IDR VND KRW are currencies that doesn't support decimal places
  if (
    currencyCode === INDONESIAN_RUPIAH ||
    currencyCode === JAPANESE_YEN ||
    currencyCode === KOREAN_WON ||
    currencyCode === VIETNAMESE_DONG
  ) {
    let localeString = INDONESIAN_CULTURE;

    switch (currencyCode) {
      case JAPANESE_YEN:
        localeString = JAPAN_CULTURE;
        break;
      case KOREAN_WON:
        localeString = KOREA_CULTURE;
        break;
      case VIETNAMESE_DONG:
        localeString = VIETNAME_CULTURE;
        break;
      default:
        localeString = INDONESIAN_CULTURE;
        break;
    }

    val = Math.ceil(Number(val));

    return Number(val).toLocaleString(localeString, {
      minimumFractionDigits: 0,
    });
  }

  return Number(val).toLocaleString(languageId, {
    minimumFractionDigits: decimal,
  });
}

export function getRuleListValue(value, field) {
  switch (field) {
    case 'monthlyCap':
      return `${value.settlementCurrency} ${value.monthlyCap}`;

    case 'createdBy':
      return `${value.createdBy} at ${dateTimeFormatter(value.createdAt)}`;

    case 'modifiedBy':
      return `${value.modifiedBy} at ${dateTimeFormatter(value.modifiedAt)}`;
    default:
      return value[field];
  }
}

export function getRuleListFormattedValue(value, field) {
  switch (field) {
    case 'transactionRatio':
      return `${value}%`;

    case 'effectiveStartDate':
    case 'effectiveEndDate':
      return dateFormatter(value);

    default:
      return value;
  }
}

export function getRuleListAlignment(field) {
  switch (field) {
    case 'monthlyCap':
    case 'transactionRatio':
    case 'effectiveStartDate':
    case 'effectiveEndDate':
      return 'right';

    default:
      return 'center';
  }
}

export const createEmptyWeightage = () => ({
  currency: '',
  percentage: 0,
});

export const getPercentageTotal = (list) => {
  const arr = !Array.isArray(list)
    ? Object.keys(list).map((key) => ({
        currency: key,
        percentage: list[key],
      }))
    : list;

  return arr.reduce(
    (prev, curr) =>
      // NOTE: check why percentage comes as string here
      prev + (parseFloat(curr.percentage) || 0),
    0,
  );
};

export const convertWeightageArrayToMap = (list = []) =>
  list.reduce((prev, curr) => {
    if (!curr.currency || !curr.percentage) return prev;

    return {
      ...prev,
      [curr.currency]: parseFloat(curr.percentage),
    };
  }, {});

export function getDynamicRuleListAlignment(field) {
  switch (field) {
    case 'effectiveStartDate':
    case 'effectiveEndDate':
      return 'right';

    default:
      return 'center';
  }
}

export function getDynamicRuleListValue(value, field) {
  switch (field) {
    case 'createdBy':
      return `${value.createdBy} at ${dateTimeFormatter(value.createdAt)}`;

    case 'modifiedBy':
      return `${value.modifiedBy} at ${dateTimeFormatter(value.modifiedAt)}`;
    default:
      return value[field];
  }
}

export function getDynamicRuleListFormattedValue(value, field) {
  switch (field) {
    case 'effectiveStartDate':
    case 'effectiveEndDate':
      return dateFormatter(value);

    default:
      return value;
  }
}

export const createEmptyDynamicRuleCondition = () => ({
  type: '',
  parameterName: '',
  operator: '',
  parameterValue: '',
  optional: false,
});

export const createEmptyDynamicRuleOutput = () => ({
  key: '',
  value: '',
});

export const parseJson = (input) => {
  try {
    const result = JSON.parse(input);
    return { parsed: true, result };
  } catch (e) {
    return { parsed: false, result: {} };
  }
};

const getDynamicRuleOutputPayload = ({ isJsonView, keyValue = [], json }) => {
  if (isJsonView) {
    const { result } = parseJson(json);
    return result;
  }

  const output = keyValue.reduce((result, { key, value }) => {
    if (!key || !value) return result;

    return {
      ...result,
      [key]: value,
    };
  }, {});

  return output;
};

const getDynamicRuleConditionPayload = (conditions = []) =>
  conditions.filter(
    ({ type, parameterName, parameterValue, operator }) =>
      type && parameterName && parameterValue && operator,
  );

const isNestedObject = (obj) =>
  Object.keys(obj)
    .map((key) => typeof obj[key])
    .some((t) => t === 'object');

export const getCreateDynamicRulePayload = (
  { details, conditions: conditionsSection, output: outputSection },
  user,
) => {
  const { type, subType, effectiveStartDate, effectiveEndDate } = details;
  const output = getDynamicRuleOutputPayload(outputSection);
  const conditions = getDynamicRuleConditionPayload(conditionsSection);

  return {
    user,
    effectiveStartDate,
    effectiveEndDate,
    identifier: {
      type,
      subType,
    },
    conditions,
    output,
  };
};

export const getDynamicRuleDetailsFormValues = ({
  type,
  subType,
  effectiveStartDate,
  effectiveEndDate,
  conditions,
  output,
}) => {
  const formattedEffectiveStartDate = dayjs(effectiveStartDate).format(
    DYNAMIC_RULES_EFFECTIVE_DATE_FORMAT,
  );
  const formattedEffectiveEndDate = dayjs(effectiveEndDate).format(
    DYNAMIC_RULES_EFFECTIVE_DATE_FORMAT,
  );

  const isComplexOutput = isNestedObject(output);

  let computedOutput = {};
  if (isComplexOutput) {
    computedOutput = {
      isJsonView: true,
      keyValue: [],
      json: JSON.stringify(output, 0, 2),
    };
  } else {
    const outputKeyValue = Object.keys(output).map((key) => ({
      key,
      value: output[key],
    }));

    computedOutput = {
      isJsonView: false,
      keyValue: outputKeyValue,
      json: '',
    };
  }

  return {
    details: {
      type,
      subType,
      effectiveStartDate: formattedEffectiveStartDate,
      effectiveEndDate: formattedEffectiveEndDate,
    },
    conditions,
    output: computedOutput,
  };
};
